import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Add, Search } from '@mui/icons-material'
import { Box, Button, Card, Chip, InputAdornment, TableContainer, TextField, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn } from '../../../../../components/GRTable/GRTable'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import { useGetLanguageMapsQuery } from '../../../../api/language'
import { useGetFeatureEffectsQuery, useSaveFeatureEffectsMutation } from '../../../../api/motivations'
import { QueryStateBoundary } from '../../../../components/QueryStateBoundary/QueryStateBoundary'
import { rowActionsColumn } from '../../../../components/RowActions/RowActions'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import { buildFeatureEffectRows, featureEffectRowId, filterFeatureEffectRows, isSameFeatureEffectsMap } from '../../helpers/featureEffectHelpers'
import { motivationTypes } from '../../helpers/motivationOverrideHelpers'
import { FeatureEffectRow, FeatureEffectsChange } from '../../types/Motivations'
import { FeatureEffectEditDialog } from '../FeatureEffectEditDialog/FeatureEffectEditDialog'

type CustomProps = {
  onEdit: (row: FeatureEffectRow) => void
  onRemove: (row: FeatureEffectRow) => void
}

type DialogState = { open: boolean; row?: FeatureEffectRow }

const IdLabel: FC<{ name?: string; id: string }> = ({ name, id }) => (
  <Typography variant="body2">
    <Typography component="span" variant="body2" fontWeight="bold">
      {name || '---'}
    </Typography>
    : {id}
  </Typography>
)

export const FeatureEffectsTab: FC = () => {
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const featureEffectsQuery = useGetFeatureEffectsQuery()
  const languageMapsQuery = useGetLanguageMapsQuery()
  const [saveFeatureEffects, { isLoading: isSaving }] = useSaveFeatureEffectsMutation()
  const [search, setSearch] = useState('')
  const [dialogState, setDialogState] = useState<DialogState>({ open: false })
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<FeatureEffectRow>>()

  const enLanguageMap = languageMapsQuery.data?.en
  const featureEffects = useMemo(() => featureEffectsQuery.data ?? {}, [featureEffectsQuery.data])
  const rows = useMemo(() => buildFeatureEffectRows(featureEffects, enLanguageMap), [featureEffects, enLanguageMap])
  const filteredRows = useMemo(() => filterFeatureEffectRows(rows, search), [rows, search])
  const isLoading = featureEffectsQuery.isLoading || languageMapsQuery.isLoading
  const isError = featureEffectsQuery.isError || languageMapsQuery.isError

  const tableColumns: GRTableColumn<FeatureEffectRow, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-service-management:motivations_feature'),
        accessor: ({ row }) => <IdLabel name={row.featureName} id={row.featureLegacyId} />,
        sortable: true,
        sortAccessor: ({ row }) => (row.featureName ?? '').toLowerCase(),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { width: 240 } },
      },
      {
        labelAccessor: t('internal-service-management:motivations_choice'),
        accessor: ({ row }) => <IdLabel name={row.choiceName} id={row.choiceLegacyId} />,
        sortable: true,
        sortAccessor: ({ row }) => (row.choiceName ?? '').toLowerCase(),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { width: 180 } },
      },
      {
        labelAccessor: t('internal-service-management:motivations_effects'),
        accessor: ({ row }) => (
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {motivationTypes.map((type) => (
              <Chip
                key={type}
                size="small"
                variant={row.effects[type] ? 'filled' : 'outlined'}
                color={row.effects[type] ? 'primary' : 'default'}
                label={`${type}: ${row.effects[type] ?? '-'}`}
              />
            ))}
          </Box>
        ),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left' },
      },
      rowActionsColumn<FeatureEffectRow, CustomProps>({
        actions: ({ row, customTableProps }) => [
          { type: 'edit', label: t('internal-service-management:motivations_edit_short'), onClick: () => customTableProps?.onEdit(row) },
          { type: 'delete', label: t('internal-service-management:motivations_remove'), onClick: () => customTableProps?.onRemove(row) },
        ],
      }),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      onEdit: (row) => setDialogState({ open: true, row }),
      onRemove: (row) =>
        setConfirmDialogData({
          title: t('internal-service-management:motivations_remove_feature_effect'),
          confirmText: (
            <Typography>
              {t('internal-service-management:motivations_remove_feature_effect_confirm', {
                feature: `${row.featureName ?? '---'}: ${row.featureLegacyId}`,
                choice: `${row.choiceName ?? '---'}: ${row.choiceLegacyId}`,
              })}
            </Typography>
          ),
          actionText: t('internal-service-management:motivations_remove'),
          destructiveAction: true,
          data: row,
        }),
    }),
    [t]
  )

  const save = useCallback(
    async (change: FeatureEffectsChange) => {
      const saved = await run(saveFeatureEffects(change).unwrap(), { error: t('internal-service-management:motivations_feature_effects_failed') })
      if (!saved.ok) return false
      const { expected, stored } = saved.result
      if (!isSameFeatureEffectsMap(expected, stored)) {
        notifyError(t('internal-service-management:motivations_feature_effects_not_stored'))
        return false
      }
      const messageKey = change.type === 'remove' ? 'motivations_feature_effect_removed' : 'motivations_feature_effect_saved'
      notifySuccess(t(`internal-service-management:${messageKey}`))
      return true
    },
    [notifyError, notifySuccess, run, saveFeatureEffects, t]
  )

  const handleSave = async (row: FeatureEffectRow) => {
    if (await save({ type: 'save', row, original: dialogState.row })) setDialogState({ open: false })
  }

  const handleConfirmRemove = (confirmed?: ConfirmDialogData<FeatureEffectRow>) => {
    setConfirmDialogData(undefined)
    const row = confirmed?.data
    if (row?.featureLegacyId) save({ type: 'remove', row })
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
        <TextField
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('internal-service-management:motivations_filter_features')}
          inputProps={{ 'aria-label': t('internal-service-management:motivations_filter_features') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ width: 360 }}
        />
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialogState({ open: true })} disabled={isLoading || isError || isSaving}>
          {t('internal-service-management:motivations_add_feature_effect')}
        </Button>
      </Box>

      <QueryStateBoundary
        isLoading={isLoading}
        isError={isError}
        errorText={t('internal-service-management:motivations_feature_effects_load_failed')}
        skeletonHeight={480}
      >
        <TableContainer component={Card}>
          <GRTable
            rows={filteredRows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey={featureEffectRowId}
            customProps={customProps}
            noRowsLabel={t('internal-common:no_rows')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <FeatureEffectEditDialog
        open={dialogState.open}
        row={dialogState.row}
        featureEffects={featureEffects}
        languageMap={enLanguageMap}
        isSaving={isSaving}
        onClose={() => setDialogState({ open: false })}
        onSave={handleSave}
      />
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirmRemove} />
    </Box>
  )
}
